import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import GenericSelect from '../reutilizables/genericSelect/GenericSelect'
import ListaVentaFormModal from '../reutilizables/listaVenta/ListaVentaFormModal'
import {
  buildListaVentaInitialValues,
  validateListaVentaForm
} from '../reutilizables/listaVenta/listaVentaFormUtils'
import * as listaVentaService from '../servicios/listaVentaService'
import styles from './ListaDeVentas.module.css'

const ListaDeVentas = ({
  onChangeState,
  onListaVentaChange,
  onEliminarLista,
  idVendedor,
  vieneDeAdministrar,
  actualizarPantallaDetalle,
  value,
  publicacion
}) => {
  const [listas, setListas] = useState([])
  const [todasLasListas, setTodasLasListas] = useState([])
  const [listasCargadas, setListasCargadas] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [nuevaLista, setNuevaLista] = useState(buildListaVentaInitialValues())
  const [errores, setErrores] = useState({})
  const [mostrarListaVencida, setMostrarListaVencida] = useState(false)
  const [nombreListaVencida, setNombreListaVencida] = useState('')
  const [idListaSincronizada, setIdListaSincronizada] = useState(null)

  const mapearLista = (lista) => ({
    value: lista.idListaVenta,
    label: lista.nombre,
    vigenciaDesde: formatearFecha(lista.vigenciaDesde, 'YYYY-MM-DD'),
    vigenciaHasta: formatearFecha(lista.vigenciaHasta, 'YYYY-MM-DD')
  })

  useEffect(() => {
    setIdListaSincronizada(null)
    setMostrarListaVencida(false)
    setNombreListaVencida('')
  }, [publicacion?.idPublicacion])

  useEffect(() => {
    const obtenerListas = async () => {
      try {
        const listasRtn = await listaVentaService.buscarListaPorVendedor(idVendedor)
        const listasMapeadas = listasRtn.data.map(mapearLista)
        setTodasLasListas(listasMapeadas)
        setListas(
          listasMapeadas.filter(
            (element) =>
              !element.vigenciaHasta || validarRangoFechas(new Date(), element.vigenciaHasta, 'day')
          )
        )
        setListasCargadas(true)
      } catch (error) {
        const mensaje = 'Ocurrió un error al cargar las listas de venta.'
        toastErrorLog(error, mensaje, 'listaVenta')
        setListasCargadas(true)
      }
    }
    if (idVendedor) {
      obtenerListas()
    }
  }, [idVendedor, actualizarPantallaDetalle])

  useEffect(() => {
    if (!listasCargadas || Number(value) <= 0) {
      setIdListaSincronizada(null)
      return
    }

    const listaSeleccionada = listas.find((item) => Number(item.value) === Number(value))
    const listaSeleccionadaExiste = Boolean(listaSeleccionada)
    if (!listaSeleccionadaExiste) {
      const listaVencida = todasLasListas.find((item) => Number(item.value) === Number(value))
      setNombreListaVencida(listaVencida?.label || '')
      setIdListaSincronizada(null)
      onChangeState('idListaVenta', 0)
      if (onListaVentaChange) {
        onListaVentaChange(null)
      }
      setMostrarListaVencida(true)
      return
    }

    if (onListaVentaChange && Number(idListaSincronizada) !== Number(value)) {
      onListaVentaChange(listaSeleccionada)
      setIdListaSincronizada(Number(value))
    }
  }, [
    idListaSincronizada,
    listas,
    listasCargadas,
    onChangeState,
    onListaVentaChange,
    todasLasListas,
    value
  ])

  const abrirModal = () => {
    setNuevaLista(buildListaVentaInitialValues())
    setErrores({})
    setShowModal(true)
  }

  const cerrarModal = () => {
    setShowModal(false)
    setErrores({})
  }

  const guardarLista = async () => {
    try {
      const nuevosErrores = validateListaVentaForm({
        values: nuevaLista,
        listas
      })

      setErrores(nuevosErrores)
      if (Object.keys(nuevosErrores).length > 0) {
        return
      }

      const lista = {
        idVendedor,
        nombre: nuevaLista.nombre.trim(),
        idListaVentaEstado: 2,
        vigenciaDesde: nuevaLista.vigenciaDesde,
        vigenciaHasta: nuevaLista.vigenciaHasta,
        ofertaDesde: null,
        ofertaHasta: null,
        porcDcto: null
      }

      const nuevaListaCreada = await listaVentaService.insertarLista(lista)
      onChangeState('idListaVenta', nuevaListaCreada.data.idListaVenta)
      const listasActualizadas = await refrescasListas()
      const listaSeleccionada = listasActualizadas.find(
        (item) => Number(item.value) === Number(nuevaListaCreada.data.idListaVenta)
      )
      if (onListaVentaChange) {
        onListaVentaChange(listaSeleccionada || null)
      }
      cerrarModal()
    } catch (error) {
      const mensaje = 'Ocurrió un error al guardar la lista.'
      toastErrorLog(error, mensaje, 'listaVenta')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNuevaLista((prev) => ({
      ...prev,
      [name]: value
    }))

    if (errores[name]) {
      setErrores((prev) => {
        const nuevos = { ...prev }
        delete nuevos[name]
        return nuevos
      })
    }
  }

  const refrescasListas = async () => {
    try {
      const listasRtn = await listaVentaService.buscarListaPorVendedor(idVendedor)
      const listasMapeadas = listasRtn.data.map(mapearLista)
      const listasVigentesMapeadas = listasMapeadas.filter(
        (lista) =>
          !lista.vigenciaHasta || validarRangoFechas(new Date(), lista.vigenciaHasta, 'day')
      )

      setTodasLasListas(listasMapeadas)
      setListas(listasVigentesMapeadas)
      setListasCargadas(true)
      return listasVigentesMapeadas
    } catch (error) {
      const mensaje = 'Ocurrió un error al refrescar las listas de venta.'
      toastErrorLog(error, mensaje, 'listaVenta')
      setListasCargadas(true)
      return []
    }
  }

  const handleSelectLista = (nuevoValor) => {
    setMostrarListaVencida(false)
    setNombreListaVencida('')
    setIdListaSincronizada(Number(nuevoValor) > 0 ? Number(nuevoValor) : null)
    onChangeState('idListaVenta', nuevoValor)
    if (!onListaVentaChange) {
      return
    }

    const listaSeleccionada = listas.find((item) => Number(item.value) === Number(nuevoValor))
    onListaVentaChange(listaSeleccionada || null)
  }

  const listaSeleccionadaVigente = listas.some((item) => Number(item.value) === Number(value))
  const valorSelect = Number(value) > 0 && !listaSeleccionadaVigente ? '' : value || ''

  return (
    <>
      {!vieneDeAdministrar && (
        <p className={styles.descripcionCuadroPublicacion}>
          Indica si el artículo pertenece a una Lista de Ventas.
        </p>
      )}
      <div className={styles.listaDeVentasContainer}>
        <div className={styles.selectorWrapper}>
          <GenericSelect
            options={listas}
            value={valorSelect}
            label="Elegir Lista:"
            id="idListaVenta"
            className={styles.selectListaVenta}
            onChange={handleSelectLista}
          />
          {Number(value) > 0 && (
            <button
              type="button"
              className={styles.botonEliminarLista}
              title="Eliminar lista de la publicación"
              onClick={() => {
                handleSelectLista(0)
                onEliminarLista?.()
              }}
            >
              <i className="bi bi-trash" aria-hidden="true" />
            </button>
          )}
          {mostrarListaVencida && (
            <span className="warningMessage">
              {`La lista de venta "${nombreListaVencida}" está vencida y se desasignó de la
              publicación.`}
            </span>
          )}
        </div>
        <div className={styles.crearListaVenta}>
          <button type="button" className={styles.botonCrearLista} onClick={abrirModal}>
            <span>Crear una nueva lista</span>
            <i className={`fa fa-plus ${styles.iconoPlus}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      <ListaVentaFormModal
        show={showModal}
        title="Crear lista de venta"
        values={nuevaLista}
        errores={errores}
        onChange={handleInputChange}
        onClose={cerrarModal}
        onConfirm={guardarLista}
      />
    </>
  )
}

ListaDeVentas.propTypes = {
  onChangeState: PropTypes.func.isRequired,
  onListaVentaChange: PropTypes.func,
  onEliminarLista: PropTypes.func,
  idVendedor: PropTypes.number.isRequired,
  vieneDeAdministrar: PropTypes.bool,
  actualizarPantallaDetalle: PropTypes.bool,
  value: PropTypes.number.isRequired,
  publicacion: PropTypes.shape({
    idPublicacion: PropTypes.number
  })
}

export default ListaDeVentas
